import React, { useEffect } from 'react';
import StatBox from '../ui/StatBox';
import Confetti from '../ui/Confetti';
import { api } from '../../utils/api';
import { User } from '../../types';

export type GameState = 'ready' | 'playing' | 'over';

interface GameIntroProps {
  icon: string;
  title: string;
  skill: string;
  accent: string; // tailwind text color for the skill, e.g. 'text-indigo-600'
  button: string; // tailwind bg + hover classes for the start button
  onStart: () => void;
  children: React.ReactNode; // how-to-play text or extra options
}

export const GameIntro: React.FC<GameIntroProps> = ({ icon, title, skill, accent, button, onStart, children }) => (
  <div className="relative text-center py-8 px-4 rounded-3xl bg-white/70 shadow-inner">
    <div className="relative inline-block mb-4">
      <span className="fun-float absolute -left-8 -top-2 text-2xl" aria-hidden="true">✨</span>
      <span className="fun-float absolute -right-8 top-4 text-xl" style={{ animationDelay: '1.5s' }} aria-hidden="true">⭐</span>
      <div className="fun-bounce inline-flex w-28 h-28 items-center justify-center rounded-full bg-gradient-to-br from-yellow-200 via-pink-200 to-sky-200 shadow-lg ring-4 ring-white text-7xl" aria-hidden="true">{icon}</div>
    </div>
    <h3 className="text-3xl font-black mb-2 bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 bg-clip-text text-transparent">{title}</h3>
    <p className="text-gray-700 max-w-md mx-auto mb-2 font-medium">
      Builds <span className={`font-bold ${accent}`}>{skill}</span>
    </p>
    <div className="text-gray-600 text-sm max-w-md mx-auto mb-6">{children}</div>
    <button onClick={onStart} className={`fun-shine px-10 py-3.5 text-white rounded-full font-black text-2xl shadow-lg ring-4 ring-white transition-transform hover:scale-105 active:scale-95 ${button}`}>
      ▶ Let's Play!
    </button>
  </div>
);

interface GameOverProps {
  title?: string;
  message?: string;
  stats: { label: string; value: string | number }[];
  button: string;
  onRestart: () => void;
  stars?: number; // 0-3 star rating; omitted = no stars shown
  children?: React.ReactNode;
}

export const GameOver: React.FC<GameOverProps> = ({ title = 'Game Over!', message, stats, button, onRestart, stars, children }) => (
  <div className="relative text-center py-8 px-4 rounded-3xl bg-white/70 shadow-inner">
    {(stars === undefined || stars > 0) && <Confetti />}
    <h3 className="fun-pop text-4xl font-black bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 bg-clip-text text-transparent">{title}</h3>
    {message && <p className="text-lg font-semibold text-gray-600 mt-1">{message}</p>}
    {stars !== undefined && <StarRating stars={stars} />}
    {children}
    <div className="flex flex-wrap justify-center gap-4 mt-6">
      {stats.map((s, i) => (
        <div key={s.label} className="fun-pop" style={{ animationDelay: `${0.4 + i * 0.12}s` }}>
          <StatBox label={s.label} value={s.value} />
        </div>
      ))}
    </div>
    <button onClick={onRestart} className={`fun-shine mt-8 px-8 py-3 text-white rounded-full font-black text-xl shadow-lg ring-4 ring-white transition-transform hover:scale-105 active:scale-95 ${button}`}>
      🔁 Play Again
    </button>
  </div>
);

export const StarRating: React.FC<{ stars: number }> = ({ stars }) => (
  <div className="flex justify-center gap-2 mt-4" role="img" aria-label={`${stars} out of 3 stars`}>
    {[0, 1, 2].map(i => (
      <span key={i} className={`fun-pop text-5xl ${i < stars ? 'drop-shadow-[0_2px_6px_rgba(250,204,21,0.8)]' : 'grayscale opacity-30'}`} style={{ animationDelay: `${0.2 + i * 0.25}s` }} aria-hidden="true">⭐</span>
    ))}
  </div>
);

// Records a finished game for signed-in students (guests have no account to save to).
export function useSaveGameResult(user: User | undefined, gameName: string, gameState: GameState, highScore: number) {
  useEffect(() => {
    if (gameState === 'over' && user && user.user_id !== 'guest') {
      api.post('/api/v1/student/game-progress', { game_name: gameName, high_score: highScore, increment_attempts: true }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);
}

// Highest unlocked level (0-based) for a game. Signed-in students keep it on
// their account so it follows them to any computer; guests keep it in this
// browser. `loaded` turns true once the saved level is known.
export function useLevelProgress(gameId: string, user?: User): [number, (level: number) => void, boolean] {
  const isStudent = !!user && user.user_id !== 'guest';
  const storageKey = `typeogo-level-${gameId}`;
  const [unlocked, setUnlocked] = React.useState(() => {
    if (isStudent) return 0;
    try { return Math.max(0, parseInt(localStorage.getItem(storageKey) || '0', 10) || 0); } catch { return 0; }
  });
  const [loaded, setLoaded] = React.useState(!isStudent);

  useEffect(() => {
    if (!isStudent) return;
    let cancelled = false;
    api.get<{ unlocked_level?: number }>(`/api/v1/student/game-progress/${gameId}`)
      .then(r => { if (!cancelled) setUnlocked(prev => Math.max(prev, r.unlocked_level || 0)); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [gameId, isStudent]);

  const unlock = (level: number) => {
    if (level <= unlocked) return;
    setUnlocked(prev => Math.max(prev, level));
    if (isStudent) {
      api.post('/api/v1/student/game-progress', { game_name: gameId, unlocked_level: level }).catch(() => {});
    } else {
      try { localStorage.setItem(storageKey, String(level)); } catch { /* storage unavailable */ }
    }
  };
  return [unlocked, unlock, loaded];
}

interface LevelPickerProps {
  levels: { name: string; icon: string }[];
  unlocked: number;
  selected: number;
  onSelect: (level: number) => void;
  label?: string; // shown above each level's name, e.g. WORLD 1
}

export const LevelPicker: React.FC<LevelPickerProps> = ({ levels, unlocked, selected, onSelect, label = 'WORLD' }) => (
  <div className="mt-2 flex flex-wrap justify-center gap-2">
    {levels.map((lv, i) => {
      const locked = i > unlocked;
      return (
        <button key={lv.name} type="button" disabled={locked} onClick={() => onSelect(i)} aria-pressed={selected === i}
          className={`relative flex flex-col items-center w-24 px-2 py-2 rounded-xl bg-white shadow transition-transform
            ${locked ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'} ${selected === i ? 'ring-4 ring-violet-400' : 'ring-1 ring-gray-200'}`}>
          <span className="text-3xl leading-none">{locked ? '🔒' : lv.icon}</span>
          <span className="mt-1 text-[11px] font-black text-gray-500">{label} {i + 1}</span>
          <span className="text-xs font-bold text-gray-700 leading-tight">{lv.name}</span>
        </button>
      );
    })}
  </div>
);

// True for keystrokes a game should treat as typing (not shortcuts or modifier combos).
export const isTypingKey = (e: KeyboardEvent) => e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
